import type { Action, Reducer } from 'redux';
import SitesService from '../service/sites';
import { IApplicationState, IAppThunkAction, IAnalyticsAction } from '.';
import ISite from '../interfaces/ISite';
import IBookingOptions from '../interfaces/IBookingOptions';
import { actionCreators as UIActions } from '../store/UI';
import { actionCreators as BookingActions } from '../store/Booking';
import ILocalisedContent from '../interfaces/ILocalisedContent';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import { sanitiseAccessCode } from '../helpers/routes';
import { appInsights } from "../Logging/appInsights";
import { ILoadedPtvDetailsAction, LOADED_PTV_DETAILS } from './PremiumPtv';

export interface ISiteState {
    accessCode?: string;
    preselectedSite?: string;
    selectedSite?: ISite;
    selectedSiteGtmKey?: string;
    bookingOptions?: IBookingOptions;
    nearestSite?: ISite;
    selectedSiteUI: boolean;
    sites: Array<ISite>;
    sitesLoading: boolean;
    sitesLoaded: boolean;
    policies?: Array<ILocalisedContent>;
    globalSite?: ISite;
    globalSiteLoading: boolean;
    globalSiteLoaded: boolean;
}

export const ADD_ACCESS_CODE: 'ADD_ACCESS_CODE' = 'ADD_ACCESS_CODE';
export const NEAREST_SITE: 'NEAREST_SITE' = 'NEAREST_SITE';
export const SITE_SELECTED: 'SITE_SELECTED' = 'SITE_SELECTED';
export const SITE_DESELECTED: 'SITE_DESELECTED' = 'SITE_DESELECTED';
export const SITES_LOADING: 'SITES_LOADING' = 'SITES_LOADING';
export const SITES_LOADED: 'SITES_LOADED' = 'SITES_LOADED';
export const SITES_LOAD_FAILED: 'SITES_LOAD_FAILED' = 'SITES_LOAD_FAILED';
export const PRESELECT_SITE: 'PRESELECT_SITE' = 'PRESELECT_SITE';
export const SITE_POLICIES_LOADING: 'SITE_POLICIES_LOADING' = 'SITE_POLICIES_LOADING';
export const SITE_POLICIES_LOADED: 'SITE_POLICIES_LOADED' = 'SITE_POLICIES_LOADED';
export const BOOKING_OPTIONS_LOADED: 'BOOKING_OPTIONS_LOADED' = 'BOOKING_OPTIONS_LOADED';

export const GLOBAL_SITE_LOADING: 'GLOBAL_SITE_LOADING' = 'GLOBAL_SITE_LOADING';
export const GLOBAL_SITE_LOADED: 'GLOBAL_SITE_LOADED' = 'GLOBAL_SITE_LOADED';
export const GLOBAL_SITE_LOAD_FAILED: 'GLOBAL_SITE_LOAD_FAILED' = 'GLOBAL_SITE_LOAD_FAILED';

export interface IGetSitePoliciesAction extends Action {
    type: typeof SITE_POLICIES_LOADING;
}

export interface IGetSitePoliciesLoadedAction extends Action {
    type: typeof SITE_POLICIES_LOADED;
    policies: Array<ILocalisedContent>;
}

export interface IPreselectSiteAction extends Action {
    type: typeof PRESELECT_SITE;
    site: string;
}

export interface ISiteSelectedAction extends IAnalyticsAction {
    type: typeof SITE_SELECTED;
    site: ISite;
    selectedSiteGtmKey?: string;
    hasGiftVoucher: boolean;
}

export interface ISiteDeselectedAction extends IAnalyticsAction {
    type: typeof SITE_DESELECTED;
}

export interface ISitesLoadingAction extends Action {
    type: typeof SITES_LOADING;
}

export interface ISitesLoadedAction extends Action {
    type: typeof SITES_LOADED;
    sites: Array<ISite>;
    preSelectedSite?: ISite;
}

export interface ISitesLoadFailedAction extends Action {
    type: typeof SITES_LOAD_FAILED;
}

export interface ISetNearestSiteAction extends IAnalyticsAction {
    type: typeof NEAREST_SITE;
    site: ISite;
    selectedSiteGtmKey?: string;
}

export interface IAddAccessCode extends Action {
    type: typeof ADD_ACCESS_CODE;
    accessCode: string;
}

export interface IBookingOptionsAction extends Action {
    type: typeof BOOKING_OPTIONS_LOADED;
    bookingOptions: IBookingOptions;
}

export interface IGlobalSiteLoadingAction extends Action {
    type: typeof GLOBAL_SITE_LOADING;
}

export interface IGlobalSiteLoadedAction extends Action {
    type: typeof GLOBAL_SITE_LOADED;
    site: ISite;
}

export interface IGlobalSiteLoadFailedAction extends Action {
    type: typeof GLOBAL_SITE_LOAD_FAILED;
}


export type KnownSiteActions =
    | IAddAccessCode
    | ISiteSelectedAction
    | ISiteDeselectedAction
    | ISitesLoadingAction
    | ISitesLoadedAction
    | ISitesLoadFailedAction
    | IGetSitePoliciesAction
    | IGetSitePoliciesLoadedAction
    | ISetNearestSiteAction
    | IPreselectSiteAction
    | IBookingOptionsAction
    | IGlobalSiteLoadingAction
    | IGlobalSiteLoadedAction
    | IGlobalSiteLoadFailedAction
	| ILoadedPtvDetailsAction
    ;

export const actionCreators = {
    loadSites: (): IAppThunkAction<KnownSiteActions> => (dispatch: any, getState: any) => {
        const appState: IApplicationState = getState();
        if (!appState) return;

        // A preview access code (from the /book-now/:venue/:accessCode deep link) can reveal a
        // pre-launch venue that is otherwise hidden from the public /sites list.
        const accessCode = appState.sites.accessCode || undefined;
        const preselected = appState.sites.preselectedSite;

        // If we already loaded the public list but the preselected venue isn't in it and we now have a
        // code, reload with the code so the hidden venue can be revealed.
        const preselectedResolvable =
            !preselected ||
            (appState.sites.sites?.some(
                (x) => x !== null && (x.urlKey?.startsWith(preselected) || x.siteId === parseInt(preselected))
            ) ??
                false);
        const needsPreviewReload = !!accessCode && !!preselected && appState.sites.sitesLoaded && !preselectedResolvable;

        if (!appState.sites.sitesLoaded || needsPreviewReload) {
            SitesService.GetSites(accessCode)
                .then((data) => {
                    if (appState.sites.preselectedSite) {
                        const targetKey = appState.sites.preselectedSite;
                        const site = data.find((x) => x !== null && x.urlKey?.startsWith(targetKey));

                        dispatch({ type: SITES_LOADED, sites: data, preSelectedSite: site });
                    } else dispatch({ type: SITES_LOADED, sites: data });
                })
                .catch((error: Error) => {
                    dispatch({ type: SITES_LOAD_FAILED});
                    dispatch(UIActions.displayError('errors.noSites'));
                     appInsights?.trackException(
                         { error: new Error(`Error loading sites`), severityLevel: 5},
                         { exception: error, sourceFile: "Sites.ts", product: "booking" });
                });

            dispatch({ type: SITES_LOADING });
        }
    },
    loadGlobleSite: (): IAppThunkAction<KnownSiteActions> => (dispatch: any, getState: any) => {
        const appState: IApplicationState = getState();
        if (appState && !appState.sites.globalSiteLoaded) {
            SitesService.GetGlobalVenue()
                .then((data) => {
                        dispatch({ type: GLOBAL_SITE_LOADED, site: data});
                })
                .catch((error: Error) => {
                    dispatch({ type: GLOBAL_SITE_LOAD_FAILED });
                    dispatch(UIActions.displayError('errors.noGlobalSites'));
                    appInsights?.trackException(
                        { error: new Error(`Error loading global site`), severityLevel: 5 },
                        { exception: error, sourceFile: "Sites.ts", product: "booking" });
                });

            dispatch({ type: GLOBAL_SITE_LOADING });
        }
    },

    loadSitePolicies: (): IAppThunkAction<KnownSiteActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        if (appState && appState.sites.selectedSite) {
            SitesService.GetSitePolicies(appState.sites.selectedSite.siteId).then((data) => {
                dispatch({ type: SITE_POLICIES_LOADED, policies: data });
            });

            dispatch({ type: SITE_POLICIES_LOADING });
        }
    },

    preselectSite:
        (site: string): IAppThunkAction<KnownSiteActions> =>
        (dispatch, _getState) => {
            dispatch({ type: PRESELECT_SITE, site: site.toLowerCase() });
        },

    addAccessCode:
        (accessCode: string): IAppThunkAction<KnownSiteActions> =>
        (dispatch, _getState) => {
            //shared with the pre-store URL read in localStorage.ts so both agree on what
            //counts as a usable code (see sanitiseAccessCode for the positional-segment trap)
            const cleaned = sanitiseAccessCode(accessCode);
            if (!cleaned) return;

            dispatch({ type: ADD_ACCESS_CODE, accessCode: cleaned });
        },

    siteSelected:
        (site: ISite | undefined, nearest: boolean, nearby: boolean = false, locationWidget: boolean = false): IAppThunkAction<KnownSiteActions> =>
            (dispatch: any, _getState: any) => {

                // if the site is undefined, assume we are 'de-selecting' a venue
                if (site) {
                    // Create a new GTM Key if the site uses tag manager
                    const gtmKey = site.siteMetadata.googleTagManagerKey ? `dataLayer${site.code.replace('-', '')}` : undefined;

                    if (nearest) {
                        dispatch({
                            type: NEAREST_SITE,
                            site: site,
                            googleTagManagerEvent: GoogleTagManagerEvents.SiteSelected(site, true, nearby, locationWidget),
                            selectedSiteGtmKey: gtmKey,
                        } as ISetNearestSiteAction);
                    }
                    else {
                        dispatch({
                            type: SITE_SELECTED,
                            site: site,
                            googleTagManagerEvent: GoogleTagManagerEvents.SiteSelected(site, false, nearby, locationWidget),
                            selectedSiteGtmKey: gtmKey,
                        } as ISiteSelectedAction);
                    }
                }
                else {
                    console.log(`siteSelected with no data, clearing site `);

                    dispatch({
                        type: SITE_DESELECTED
                    } as ISiteDeselectedAction);
                }
        },

    loadSiteBookingOptions:
        (site: ISite, key: string | undefined): IAppThunkAction<KnownSiteActions> =>
        (dispatch: any, _getState: any) => {
            SitesService.GetSiteBookingOptions(site.siteId, key).then((data): any => {
                dispatch({ type: BOOKING_OPTIONS_LOADED, bookingOptions: data });

                if (data.priceTierSet !== undefined) {
                    dispatch(BookingActions.recordPriceSet(site.urlKey, data.priceTierSet, 'server'));
                }
            });
        },
};

const unloadedState: ISiteState = {
    sitesLoading: false,
    sites: [],
    selectedSite: undefined,
    sitesLoaded: false,
    nearestSite: undefined,
    selectedSiteUI: false,
    bookingOptions: undefined,
    globalSite: undefined,
    globalSiteLoading: false,
    globalSiteLoaded: false,
};

export const reducer: Reducer<ISiteState> = (state: ISiteState | undefined, incomingAction: Action): ISiteState => {
    if (state === undefined) return unloadedState;

    const action: KnownSiteActions = incomingAction as KnownSiteActions;
    switch (action.type) {
        case SITES_LOADING:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: state.selectedSiteUI,
                sitesLoading: true,
                sitesLoaded: false,
                sites: state.sites,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case SITES_LOADED:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: state.selectedSiteUI,
                sitesLoading: false,
                sitesLoaded: true,
                sites: action.sites,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case SITES_LOAD_FAILED:
            return {
                ...state,
                sitesLoading: false,
                sitesLoaded: false,
            };
        case SITE_SELECTED:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: action.site,
                selectedSiteGtmKey: action.selectedSiteGtmKey,
                selectedSiteUI: !action.site.useExternalBookingUrl || !!action.site.previewAccess,
                sitesLoading: false,
                sites: state.sites,
                sitesLoaded: true,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case SITE_DESELECTED:
            return {
                ...state,
                preselectedSite: undefined,
                selectedSiteUI: false,
                selectedSite: undefined,
                selectedSiteGtmKey: undefined,
            };
        case NEAREST_SITE:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: action.site,
                selectedSiteGtmKey: action.selectedSiteGtmKey,
                selectedSiteUI: true,
                sitesLoading: state.sitesLoading,
                sitesLoaded: state.sitesLoaded,
                sites: state.sites,
                nearestSite: action.site,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case PRESELECT_SITE:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: action.site,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: true,
                sitesLoading: state.sitesLoading,
                sitesLoaded: state.sitesLoaded,
                sites: state.sites,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case SITE_POLICIES_LOADED:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: true,
                sitesLoading: state.sitesLoading,
                sitesLoaded: state.sitesLoaded,
                sites: state.sites,
                nearestSite: state.nearestSite,
                policies: action.policies,
                bookingOptions: state.bookingOptions,
            };
        case ADD_ACCESS_CODE:
            return {
                ...state, 
                accessCode: action.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: state.selectedSiteUI,
                sitesLoading: state.sitesLoading,
                sitesLoaded: state.sitesLoaded,
                sites: state.sites,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: state.bookingOptions,
            };
        case BOOKING_OPTIONS_LOADED:
            return {
                ...state, 
                accessCode: state.accessCode,
                preselectedSite: state.preselectedSite,
                selectedSite: state.selectedSite,
                selectedSiteGtmKey: state.selectedSiteGtmKey,
                selectedSiteUI: state.selectedSiteUI,
                sitesLoading: state.sitesLoading,
                sitesLoaded: state.sitesLoaded,
                sites: state.sites,
                nearestSite: state.nearestSite,
                policies: state.policies,
                bookingOptions: action.bookingOptions,
            };
        case GLOBAL_SITE_LOADED:
            return {
                ...state,
                globalSiteLoading: false,
                globalSiteLoaded: true,
                globalSite: action.site
            };
        case GLOBAL_SITE_LOADING:
            return {
                ...state,
                globalSiteLoading: true,
                globalSiteLoaded: false,
                globalSite: undefined
            };
        case GLOBAL_SITE_LOAD_FAILED:
            return {
                ...state,
                globalSiteLoading: false,
                globalSiteLoaded: false,
                globalSite: undefined
            };

        case LOADED_PTV_DETAILS:
            return {
                ...state,
                selectedSite: action.purchaseDetails.site
            };
        default:
            return state;
    }
};
